/**
 * FP3-029B — Admin Schools / Campuses surface.
 *
 *   /admin/schools
 *
 * Lists schools/campuses scoped by role. Super admin can pick an org
 * and edit billing/access posture. Org admins see only their own
 * org's schools and may rename them. Providers are 403 at the API.
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Buildings,
  Plus,
  Tag,
  Users,
  GraduationCap,
  PencilSimple,
  Eye,
  CaretDown,
  CaretUp,
  ArrowRight,
  Trash,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  useAdminSchools,
  invalidateAfterMutation,
} from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
import AdminShell from "./AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// FP3-043 Phase C — Super-admin "show demo/test data" toggle + classification badges.
import ShowHiddenDataToggle from "@/components/platform/ShowHiddenDataToggle";
import ClassificationBadge from "@/components/platform/ClassificationBadge";
// FP3-105 — Super Admin permanent school deletion.
import PlatformDeleteModal from "@/components/platform/PlatformDeleteModal";

const ACCESS_TYPES = [
  "inherited",
  "pilot",
  "limited",
  "trial",
  "membership",
];
const MEMBERSHIP_STATUSES = [
  "inherited",
  "not_required",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "paused",
  "expired",
];
const BILLING_INTERVALS = [
  "inherited",
  "none",
  "monthly",
  "quarterly",
  "annual",
];

function chip(label, color = "default") {
  const tones = {
    default: { bg: "#EEF1F6", fg: "#3F4756" },
    ok: { bg: "#E8F0EB", fg: "#1E3F33" },
    warn: { bg: "#F5EFE3", fg: "#7A6A2C" },
    info: { bg: "#E7EDF3", fg: "#1F3F4F" },
  };
  const t = tones[color] || tones.default;
  return (
    <span
      className="inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full"
      style={{ backgroundColor: t.bg, color: t.fg }}
    >
      {label}
    </span>
  );
}

function accessTone(value) {
  if (!value || value === "inherited") return "default";
  if (value === "membership" || value === "active") return "ok";
  if (value === "trial" || value === "trialing") return "info";
  return "warn";
}

export default function AdminSchools() {
  const { user } = useAuth();
  const isSuperAdmin = user?.permission_role === "super_admin";
  // FP3-053 — Sidebar context selector drives the org context via
  // `?as_org=`. The local org picker on this page is now a soft
  // mirror.
  const [searchParams] = useSearchParams();
  const urlAsOrg = (searchParams.get("as_org") || "").trim();

  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState(
    urlAsOrg || (isSuperAdmin ? "" : user?.organization_id || "")
  );
  useEffect(() => {
    if (isSuperAdmin) setOrgId(urlAsOrg || "");
  }, [urlAsOrg, isSuperAdmin]);
  const [schools, setSchools] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedSchool, setExpandedSchool] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // FP3-105 — school pending permanent deletion (super_admin only).
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api
      .getOrganizations()
      .then((r) => setOrganizations(r.data || []))
      .catch(() => setOrganizations([]));
  }, [isSuperAdmin]);

  // FP3-119B — Steady-state cache for the schools list. When a super
  // admin has no org selected, the query is disabled (skips fetch).
  // When enabled, the payload renders immediately from cache on
  // revisit and refetches in the background only when stale.
  const enabled = !(isSuperAdmin && !orgId);
  const schoolsQuery = useAdminSchools(
    enabled ? { organization_id: orgId || undefined } : undefined,
    { enabled },
  );
  const loading = enabled && schoolsQuery.isPending;
  const refetching = schoolsQuery.isFetching && !schoolsQuery.isPending;
  const dataUpdatedAt = schoolsQuery.dataUpdatedAt;

  useEffect(() => {
    if (!enabled) {
      setSchools([]);
      return;
    }
    if (schoolsQuery.data) setSchools(schoolsQuery.data || []);
  }, [enabled, schoolsQuery.data]);

  useEffect(() => {
    if (schoolsQuery.isError) {
      const err = schoolsQuery.error;
      toast.error(err?.response?.data?.detail || "Could not load schools");
    }
  }, [schoolsQuery.isError, schoolsQuery.error]);


  // FP3-029B5 — Lazy-load the detail payload when admin opens a row.
  // Detail surfaces hydrated provider + student lists for fast
  // navigation back to the filtered Providers / Students pages.
  const toggleSchool = (school) => {
    if (expandedSchool === school.id) {
      setExpandedSchool(null);
      setDetail(null);
      return;
    }
    setExpandedSchool(school.id);
    setDetail(null);
    setDetailLoading(true);
    api
      .getAdminSchoolDetail(school.id)
      .then((r) => setDetail(r.data))
      .catch((err) =>
        toast.error(err?.response?.data?.detail || "Could not load school detail")
      )
      .finally(() => setDetailLoading(false));
  };

  return (
    <AdminShell
      title="Schools & Campuses"
      subtitle="Each school is the operational unit for assignments, students, and billing posture."
    >
      {isSuperAdmin && (
        <Card className="border-[#E5E3DB] rounded-2xl mb-4">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <Buildings size={16} weight="duotone" className="text-[#3F4756]" />
            <select
              data-testid="schools-org-select"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="h-9 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
            >
              <option value="">— All organizations —</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <span className="flex-1" />
            <RefreshChip
              dataUpdatedAt={dataUpdatedAt}
              isFetching={refetching}
              onRefresh={() => schoolsQuery.refetch()}
              testid="schools-refresh"
            />
            <ShowHiddenDataToggle testid="schools-show-hidden-toggle" />
            <Button
              data-testid="schools-new-btn"
              onClick={() => setCreateOpen(true)}
              className="h-9 px-3 bg-[#1E3F33] hover:bg-[#173127] text-white text-[12.5px]"
              disabled={!orgId}
            >
              <Plus size={12} className="mr-1.5" /> New school
            </Button>
          </CardContent>
        </Card>
      )}

      {!isSuperAdmin && (
        <Card className="border-[#E5E3DB] rounded-2xl mb-4">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-[12.5px] text-[#6B7280] flex items-center gap-2">
              <Buildings size={14} weight="duotone" /> Showing schools in your
              organization.
            </span>
            <div className="flex items-center gap-3">
              <RefreshChip
                dataUpdatedAt={dataUpdatedAt}
                isFetching={refetching}
                onRefresh={() => schoolsQuery.refetch()}
                testid="schools-refresh"
              />
              <Button
                data-testid="schools-new-btn"
                onClick={() => setCreateOpen(true)}
                className="h-9 px-3 bg-[#1E3F33] hover:bg-[#173127] text-white text-[12.5px]"
              >
                <Plus size={12} className="mr-1.5" /> New school
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-[12px]" data-testid="schools-table">
            <thead>
              <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                <th className="text-left px-4 py-2.5">School</th>
                <th className="text-left px-4 py-2.5">Access</th>
                <th className="text-left px-4 py-2.5">Membership</th>
                <th className="text-left px-4 py-2.5">Billing</th>
                <th className="text-left px-4 py-2.5">Providers</th>
                <th className="text-left px-4 py-2.5">Students</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-[#6B7280]">
                    {isSuperAdmin && !orgId
                      ? "Pick an organization to see its schools."
                      : "No schools yet."}
                  </td>
                </tr>
              )}
              {schools.map((s) => {
                const expanded = expandedSchool === s.id;
                return (
                  <Fragment key={s.id}>
                    <tr
                      data-testid={`school-row-${s.id}`}
                      className="border-b border-[#F8F7F2] last:border-b-0 align-top"
                    >
                      <td className="px-4 py-3 text-[#1F2937] font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{s.name}</span>
                          <ClassificationBadge
                            row={s}
                            testid={`school-classification-${s.id}`}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {chip(s.access_type || "—", accessTone(s.access_type))}
                      </td>
                      <td className="px-4 py-3">
                        {chip(
                          s.membership_status || "—",
                          accessTone(s.membership_status)
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {chip(s.billing_interval || "—")}
                      </td>
                      <td className="px-4 py-3 text-[#6B7280]">
                        <Link
                          data-testid={`school-providers-${s.id}`}
                          to={`/admin/providers?school=${s.id}`}
                          className="inline-flex items-center gap-1 hover:text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
                          title="Open Providers filtered to this school"
                        >
                          <Users size={11} weight="duotone" /> {s.provider_count}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-[#6B7280]">
                        <Link
                          data-testid={`school-students-${s.id}`}
                          to={`/admin/assignments?school=${s.id}`}
                          className="inline-flex items-center gap-1 hover:text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
                          title="Open Student Assignments filtered to this school"
                        >
                          <GraduationCap size={11} weight="duotone" /> {s.student_count}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <Button
                            data-testid={`school-view-${s.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSchool(s)}
                            className="h-7 text-[11.5px] border-[#E5E3DB] text-[#3F4756]"
                            title="View detail"
                          >
                            {expanded ? (
                              <CaretUp size={11} className="mr-1" />
                            ) : (
                              <Eye size={11} className="mr-1" />
                            )}
                            {expanded ? "Hide" : "View"}
                          </Button>
                          <Button
                            data-testid={`school-edit-${s.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setEditing(s)}
                            className="h-7 text-[11.5px] border-[#E5E3DB] text-[#3F4756]"
                          >
                            <PencilSimple size={11} className="mr-1" /> Edit
                          </Button>
                          {isSuperAdmin && (
                            <Button
                              data-testid={`school-delete-${s.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleting(s)}
                              className="h-7 text-[11.5px] border-[#E3C4BE] text-[#7A1E1E] hover:bg-[#FBF3F1] hover:text-[#611414]"
                              title="Permanently delete this school"
                            >
                              <Trash size={11} className="mr-1" /> Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr
                        data-testid={`school-detail-${s.id}`}
                        className="bg-[#FAF9F4]/60 border-b border-[#F1F2F4]"
                      >
                        <td colSpan={7} className="px-4 py-4">
                          <SchoolDetail
                            schoolId={s.id}
                            detail={detail}
                            loading={detailLoading}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {createOpen && (
        <NewSchoolModal
          orgId={orgId}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            invalidateAfterMutation.schoolChanged();
          }}
        />
      )}
      {editing && (
        <EditSchoolModal
          school={editing}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidateAfterMutation.schoolChanged();
          }}
        />
      )}
      {deleting && (
        <PlatformDeleteModal
          open={!!deleting}
          onOpenChange={(o) => {
            if (!o) setDeleting(null);
          }}
          targetType="school"
          target={deleting}
          asOrg={deleting.organization_id || orgId || ""}
          onDeleted={() => {
            setDeleting(null);
            invalidateAfterMutation.schoolChanged();
          }}
        />
      )}
    </AdminShell>
  );
}

function NewSchoolModal({ orgId, isSuperAdmin, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [accessType, setAccessType] = useState("inherited");
  const [billingInterval, setBillingInterval] = useState("inherited");
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await api.createAdminSchool({
        name: name.trim(),
        organization_id: orgId,
        access_type: isSuperAdmin ? accessType : "inherited",
        billing_interval: isSuperAdmin ? billingInterval : "inherited",
      });
      toast.success("School created");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create school");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="New school / campus" onClose={onClose}>
      <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        School name
      </Label>
      <Input
        data-testid="new-school-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Lincoln Elementary"
        className="mt-1 mb-3 border-[#E5E3DB]"
      />
      {isSuperAdmin && (
        <>
          <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            Access type
          </Label>
          <select
            data-testid="new-school-access"
            value={accessType}
            onChange={(e) => setAccessType(e.target.value)}
            className="mt-1 mb-3 h-10 w-full border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white"
          >
            {ACCESS_TYPES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            Billing interval
          </Label>
          <select
            data-testid="new-school-billing"
            value={billingInterval}
            onChange={(e) => setBillingInterval(e.target.value)}
            className="mt-1 mb-4 h-10 w-full border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white"
          >
            {BILLING_INTERVALS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onClose}
          className="h-9 text-[12.5px] border-[#E5E3DB] text-[#6B7280]"
        >
          Cancel
        </Button>
        <Button
          data-testid="new-school-submit"
          onClick={save}
          disabled={submitting || !name.trim()}
          className="h-9 px-4 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white text-[12.5px]"
        >
          Create
        </Button>
      </div>
    </ModalShell>
  );
}

function EditSchoolModal({ school, isSuperAdmin, onClose, onSaved }) {
  const [name, setName] = useState(school.name || "");
  const [accessType, setAccessType] = useState(school.access_type || "inherited");
  const [membershipStatus, setMembershipStatus] = useState(
    school.membership_status || "inherited"
  );
  const [billingInterval, setBillingInterval] = useState(
    school.billing_interval || "inherited"
  );
  // FP3-030C — Optional staff email domain. Bare (no leading "@").
  const [emailDomain, setEmailDomain] = useState(
    (school.email_domain || "").replace(/^@/, "")
  );
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    setSubmitting(true);
    try {
      const update = {
        name,
        email_domain: emailDomain.trim().replace(/^@/, "").toLowerCase(),
      };
      if (isSuperAdmin) {
        update.access_type = accessType;
        update.membership_status = membershipStatus;
        update.billing_interval = billingInterval;
      }
      await api.patchAdminSchool(school.id, update);
      toast.success("School updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update school");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Edit · ${school.name}`} onClose={onClose}>
      <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        Name
      </Label>
      <Input
        data-testid="edit-school-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="mt-1 mb-3 border-[#E5E3DB]"
      />
      <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        Staff email domain
      </Label>
      <div className="flex items-center mt-1 mb-1 bg-white border border-[#E5E3DB] rounded-md focus-within:border-[#1E3F33]">
        <span className="text-[12px] text-[#6B7280] px-2 py-2 border-r border-[#E5E3DB] select-none">
          @
        </span>
        <input
          data-testid="edit-school-email-domain"
          value={emailDomain}
          onChange={(e) => setEmailDomain(e.target.value)}
          placeholder="lincoln.k12.us"
          className="flex-1 text-[13px] p-2 bg-transparent focus:outline-none"
        />
      </div>
      <p className="text-[10.5px] text-[#9AA0A6] mb-3">
        Used by Invite User to autocomplete staff emails. Leave blank if not applicable.
      </p>
      {isSuperAdmin && (
        <>
          <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            Access type
          </Label>
          <select
            data-testid="edit-school-access"
            value={accessType}
            onChange={(e) => setAccessType(e.target.value)}
            className="mt-1 mb-3 h-10 w-full border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white"
          >
            {ACCESS_TYPES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            Membership status
          </Label>
          <select
            data-testid="edit-school-membership"
            value={membershipStatus}
            onChange={(e) => setMembershipStatus(e.target.value)}
            className="mt-1 mb-3 h-10 w-full border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white"
          >
            {MEMBERSHIP_STATUSES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
            Billing interval
          </Label>
          <select
            data-testid="edit-school-billing"
            value={billingInterval}
            onChange={(e) => setBillingInterval(e.target.value)}
            className="mt-1 mb-4 h-10 w-full border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white"
          >
            {BILLING_INTERVALS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onClose}
          className="h-9 text-[12.5px] border-[#E5E3DB] text-[#6B7280]"
        >
          Cancel
        </Button>
        <Button
          data-testid="edit-school-submit"
          onClick={save}
          disabled={submitting}
          className="h-9 px-4 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white text-[12.5px]"
        >
          Save changes
        </Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div
      data-testid="schools-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-[#E5E3DB]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <Tag size={14} weight="duotone" className="text-[#1E3F33]" />
          <h3 className="text-[15px] font-semibold text-[#1F2937] heading-font">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

// FP3-029B5 — Inline detail panel rendered when admin clicks "View"
// on a school row. Surfaces hydrated provider + student lists with
// quick links back into the filtered Providers / Student Assignments
// surfaces. Keeps the Schools page useful without a separate route.
function SchoolDetail({ schoolId, detail, loading }) {
  if (loading) {
    return (
      <div className="text-[12px] text-[#6B7280] py-2">Loading detail…</div>
    );
  }
  if (!detail) return null;
  const previewProviders = (detail.providers || []).slice(0, 8);
  const previewStudents = (detail.students || []).slice(0, 12);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[12px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <Users size={12} weight="duotone" className="text-[#3F4756]" />
            Providers ({detail.provider_count})
          </h4>
          <Link
            data-testid={`school-detail-${schoolId}-open-providers`}
            to={`/admin/providers?school=${schoolId}`}
            className="inline-flex items-center gap-1 text-[11px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
          >
            Open Providers <ArrowRight size={10} />
          </Link>
        </div>
        {previewProviders.length === 0 ? (
          <p className="text-[11.5px] text-[#9AA0A6] italic">
            No providers assigned to this school yet.
          </p>
        ) : (
          <ul className="space-y-1">
            {previewProviders.map((p) => (
              <li
                key={p.provider_id || p.user_id}
                className="flex items-center justify-between text-[11.5px] text-[#1F2937]"
              >
                <Link
                  to={`/admin/providers/${p.provider_id}`}
                  className="hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
                >
                  {p.name}
                </Link>
                {p.role && (
                  <span className="text-[10px] text-[#6B7280] ml-2">
                    {p.role}
                  </span>
                )}
              </li>
            ))}
            {detail.providers.length > previewProviders.length && (
              <li className="text-[10.5px] text-[#6B7280] italic">
                + {detail.providers.length - previewProviders.length} more…
              </li>
            )}
          </ul>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[12px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <GraduationCap size={12} weight="duotone" className="text-[#3F4756]" />
            Students ({detail.student_count})
          </h4>
          <Link
            data-testid={`school-detail-${schoolId}-open-students`}
            to={`/admin/assignments?school=${schoolId}`}
            className="inline-flex items-center gap-1 text-[11px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
          >
            Open Students <ArrowRight size={10} />
          </Link>
        </div>
        {previewStudents.length === 0 ? (
          <p className="text-[11.5px] text-[#9AA0A6] italic">
            No students at this school yet.
          </p>
        ) : (
          <ul className="space-y-1">
            {previewStudents.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between text-[11.5px] text-[#1F2937]"
              >
                <span>{s.name}</span>
                {s.grade && (
                  <span className="text-[10px] text-[#6B7280] ml-2">
                    Gr {s.grade}
                  </span>
                )}
              </li>
            ))}
            {detail.students.length > previewStudents.length && (
              <li className="text-[10.5px] text-[#6B7280] italic">
                + {detail.students.length - previewStudents.length} more…
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// Quiet `CaretDown` import — reserved for the next iteration's
// expand-all affordance.
void CaretDown;

// Help future eslint to keep the `useMemo` symbol alive.
void useMemo;
